/**
 * Resize and compress a picked image file into a JPEG data URL suitable for PATCH /me (profilePhoto).
 * @param {File} file
 * @param {{ maxEdge?: number; maxDataUrlLength?: number }} [opts]
 * @returns {Promise<string>}
 */
/**
 * Larger promo images for login-screen carousel (owner upload).
 * @param {File} file
 * @returns {Promise<string>}
 */
export async function fileToBannerDataUrl(file) {
  return fileToProfilePhotoDataUrl(file, { maxEdge: 1280, maxDataUrlLength: 2_800_000 });
}

export async function fileToProfilePhotoDataUrl(file, opts = {}) {
  const maxEdge = opts.maxEdge ?? 720;
  const maxDataUrlLength = opts.maxDataUrlLength ?? 1_800_000;

  if (!file || !file.type || !file.type.startsWith('image/')) {
    throw new Error('Please choose an image file (JPG, PNG, or WebP).');
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) {
      throw new Error('Could not read image dimensions.');
    }

    const scale = Math.min(1, maxEdge / Math.max(w, h));
    const tw = Math.max(1, Math.round(w * scale));
    const th = Math.max(1, Math.round(h * scale));

    const canvas = document.createElement('canvas');
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Your browser cannot process this image.');
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, tw, th);
    ctx.drawImage(img, 0, 0, tw, th);

    let quality = 0.88;
    let dataUrl = canvas.toDataURL('image/jpeg', quality);
    while (dataUrl.length > maxDataUrlLength && quality > 0.42) {
      quality -= 0.06;
      dataUrl = canvas.toDataURL('image/jpeg', quality);
    }
    if (dataUrl.length > maxDataUrlLength) {
      throw new Error('Image is still too large. Try a smaller file or another photo.');
    }
    return dataUrl;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(objectUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not read this image file.'));
    img.src = objectUrl;
  });
}
